"use client";

import { useState } from "react";
import type { BundledLanguage } from "shiki";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface HighlightedSampleCodeTab {
  id: string;
  label: string;
  code: string;
  html: string;
  language: BundledLanguage;
}

interface SampleCodeClientProps {
  tabs: HighlightedSampleCodeTab[];
}

function CodePanel({ html }: { html: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-background/70">
      <div
        className="code-block text-sm leading-7 [&>pre.shiki]:overflow-x-auto [&>pre.shiki]:overflow-y-visible [&>pre.shiki]:rounded-[inherit] [&>pre.shiki]:border-0 [&>pre.shiki]:pr-14"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

export function SampleCodeClient({ tabs }: SampleCodeClientProps) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null);
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? null);

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

  const copyToClipboard = async () => {
    if (!activeTab) {
      return;
    }

    try {
      await navigator.clipboard.writeText(activeTab.code);
      setCopiedTab(activeTab.id);
      window.setTimeout(() => setCopiedTab(null), 2000);
    } catch (error) {
      console.error("Failed to copy snippet", error);
    }
  };

  if (!activeTab) {
    return null;
  }

  return (
    <div className="space-y-4">
      {tabs.length > 1 ? (
        <Tabs
          value={activeTab.id}
          onValueChange={setActiveTabId}
          className="w-full"
        >
          <TabsList
            className="grid h-auto w-full"
            style={{
              gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
            }}
          >
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className="py-2">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      <div className="relative">
        <CodePanel html={activeTab.html} />

        <Button
          type="button"
          variant="outline"
          size="icon"
          className="absolute right-3 top-3 h-8 w-8 bg-background/90 backdrop-blur"
          onClick={copyToClipboard}
          aria-label={`Copy ${activeTab.label}`}
        >
          {copiedTab === activeTab.id ? (
            <Check className="h-4 w-4 text-emerald-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
